import { useEffect, useRef } from 'react'
import { AlertTriangle, Info, CheckCircle, X } from 'lucide-react'

// ══════════════════════════════════════════════════════════════════════════════
// CONFIRM MODAL - Accessible confirmation dialog
// ══════════════════════════════════════════════════════════════════════════════
//
// Replaces native confirm() with a styled, accessible modal.
// Supports different variants: confirm, danger, info
//

export default function ConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'confirm', // 'confirm' | 'danger' | 'info'
}) {
  const confirmButtonRef = useRef(null)
  const modalRef = useRef(null)

  // Focus trap and keyboard handling
  useEffect(() => {
    if (!isOpen) return

    // Focus confirm button when modal opens
    confirmButtonRef.current?.focus()

    // Handle escape key
    function handleKeyDown(e) {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onClose])

  if (!isOpen) return null

  const icons = {
    confirm: <CheckCircle className="w-6 h-6 text-havn-green-600" />,
    danger: <AlertTriangle className="w-6 h-6 text-red-500" />,
    info: <Info className="w-6 h-6 text-blue-500" />,
  }

  const confirmButtonStyles = {
    confirm: 'btn-primary',
    danger: 'btn-danger',
    info: 'btn-primary',
  }

  return (
    <div
      className="fixed inset-0 z-[200] bg-black/50 flex items-center justify-center p-4 animate-fade-in"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-title"
    >
      <div
        ref={modalRef}
        className="bg-white rounded-2xl shadow-elevated w-full max-w-md animate-slide-up"
      >
        <div className="p-6">
          <div className="flex items-start gap-4">
            <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center ${
              variant === 'danger' ? 'bg-red-100' :
              variant === 'info' ? 'bg-blue-100' : 'bg-emerald-100'
            }`}>
              {icons[variant]}
            </div>
            <div className="flex-1">
              <h3
                id="confirm-title"
                className="font-display text-lg font-semibold text-havn-charcoal"
              >
                {title}
              </h3>
              <p className="mt-2 text-sm text-havn-gray-600">
                {message}
              </p>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 bg-havn-cream-50 border-t border-havn-cream-200 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="btn-secondary"
          >
            {cancelText}
          </button>
          <button
            ref={confirmButtonRef}
            onClick={() => { onConfirm(); onClose() }}
            className={confirmButtonStyles[variant]}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// INPUT MODAL - Accessible prompt replacement
// ══════════════════════════════════════════════════════════════════════════════

export function InputModal({
  isOpen,
  onClose,
  onSubmit,
  title,
  message,
  placeholder = '',
  defaultValue = '',
  submitText = 'Submit',
  cancelText = 'Cancel',
}) {
  const inputRef = useRef(null)

  useEffect(() => {
    if (!isOpen) return

    // Focus and select input when modal opens
    setTimeout(() => {
      inputRef.current?.focus()
      inputRef.current?.select()
    }, 100)

    function handleKeyDown(e) {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onClose])

  if (!isOpen) return null

  const handleSubmit = (e) => {
    e.preventDefault()
    const value = inputRef.current?.value.trim()
    if (value) {
      onSubmit(value)
      onClose()
    }
  }

  return (
    <div
      className="fixed inset-0 z-[200] bg-black/50 flex items-center justify-center p-4 animate-fade-in"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="input-title"
    >
      <div className="bg-white rounded-2xl shadow-elevated w-full max-w-md animate-slide-up">
        <form onSubmit={handleSubmit}>
          <div className="p-6">
            <h3
              id="input-title"
              className="font-display text-lg font-semibold text-havn-charcoal"
            >
              {title}
            </h3>
            {message && (
              <p className="mt-2 text-sm text-havn-gray-600">
                {message}
              </p>
            )}
            <input
              ref={inputRef}
              type="text"
              defaultValue={defaultValue}
              placeholder={placeholder}
              className="input mt-4"
              aria-label={title}
            />
          </div>

          <div className="px-6 py-4 bg-havn-cream-50 border-t border-havn-cream-200 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
            >
              {cancelText}
            </button>
            <button
              type="submit"
              className="btn-primary"
            >
              {submitText}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
